// engine/WorldGrid.ts — fixed-size tile grid with O(1) entity bucketing.
//
// Each tile holds an intrusive doubly-linked list of entity ids (the "bucket"),
// so add / remove / move are O(1) with ZERO array churn or allocation:
//   tileHead[tileIndex] -> first entity id on that tile (NIL if empty)
//   nextInTile[id], prevInTile[id] -> intrusive list links (NIL = end)
//
// Collision is tile-local and simple (per docs/DESIGN.md §2): a tile may be
// impassable, and it has a hard cap on how many ANTS it can hold. Multiple
// kinds still coexist on a tile (food + ants). No physical resolution — a
// blocked move just doesn't happen.

import { NIL } from '../types.ts';
import type { EntityId } from '../types.ts';
import type { EntitySystem } from './EntitySystem.ts';

export class WorldGrid {
  readonly width: number;
  readonly height: number;
  readonly size: number;
  /** Hard cap on ants per tile (collision). */
  readonly maxAntsPerTile: number;

  private readonly entities: EntitySystem;

  /** Head of each tile's intrusive entity list (NIL if empty). */
  private readonly tileHead: Int32Array;
  /** Intrusive forward links, indexed by entity id. */
  private readonly nextInTile: Int32Array;
  /** Intrusive backward links, indexed by entity id. */
  private readonly prevInTile: Int32Array;
  /**
   * Live count of tile-occupying ("blocking") units per tile — ants and
   * grounded organisms — used for the capacity cap. Flyers/food don't count.
   */
  private readonly antCount: Int32Array;
  /** 1 if a tile can be entered, 0 if impassable (a wall). */
  private readonly passable: Uint8Array;

  constructor(
    entities: EntitySystem,
    width: number,
    height: number,
    maxAntsPerTile: number,
  ) {
    if (width <= 0 || height <= 0) {
      throw new Error('WorldGrid dimensions must be > 0');
    }
    this.entities = entities;
    this.width = width;
    this.height = height;
    this.size = width * height;
    this.maxAntsPerTile = maxAntsPerTile;

    this.tileHead = new Int32Array(this.size).fill(NIL);
    this.nextInTile = new Int32Array(entities.capacity).fill(NIL);
    this.prevInTile = new Int32Array(entities.capacity).fill(NIL);
    this.antCount = new Int32Array(this.size);
    this.passable = new Uint8Array(this.size).fill(1);
  }

  // --- Coordinate <-> index ---------------------------------------------------

  /** O(1) (x,y) -> linear tile index. Assumes in-bounds (callers gate it). */
  index(x: number, y: number): number {
    return y * this.width + x;
  }

  /** True if (x,y) is inside the grid. */
  inBounds(x: number, y: number): boolean {
    return x >= 0 && x < this.width && y >= 0 && y < this.height;
  }

  // --- Passability ------------------------------------------------------------

  setPassable(x: number, y: number, value: boolean): void {
    this.passable[this.index(x, y)] = value ? 1 : 0;
  }

  isPassable(x: number, y: number): boolean {
    return this.passable[this.index(x, y)] === 1;
  }

  /** Live ant count on a tile (for tests / debug). */
  antCountAt(x: number, y: number): number {
    return this.antCount[this.index(x, y)] as number;
  }

  /** Head entity id of a tile's bucket (NIL if empty) — for iteration. */
  headAt(tileIndex: number): EntityId {
    return this.tileHead[tileIndex] as EntityId;
  }

  /** Next entity in the same tile bucket (NIL at end) — for iteration. */
  next(id: EntityId): EntityId {
    return this.nextInTile[id] as EntityId;
  }

  // --- Bucket membership (O(1), allocation-free) ------------------------------

  /**
   * Insert an already-positioned entity (entities.posX/posY[id]) into its
   * tile bucket. Updates ant capacity bookkeeping. O(1).
   */
  add(id: EntityId): void {
    const ti = this.index(this.entities.posX[id] as number, this.entities.posY[id] as number);
    const head = this.tileHead[ti] as number;
    this.prevInTile[id] = NIL;
    this.nextInTile[id] = head;
    if (head !== NIL) this.prevInTile[head] = id;
    this.tileHead[ti] = id;
    if (this.entities.blocks[id] === 1) {
      this.antCount[ti] = (this.antCount[ti] as number) + 1;
    }
  }

  /** Unlink an entity from its current tile bucket. O(1). */
  remove(id: EntityId): void {
    const ti = this.index(this.entities.posX[id] as number, this.entities.posY[id] as number);
    const prev = this.prevInTile[id] as number;
    const nxt = this.nextInTile[id] as number;
    if (prev !== NIL) this.nextInTile[prev] = nxt;
    else this.tileHead[ti] = nxt; // it was the head
    if (nxt !== NIL) this.prevInTile[nxt] = prev;
    this.nextInTile[id] = NIL;
    this.prevInTile[id] = NIL;
    if (this.entities.blocks[id] === 1) {
      this.antCount[ti] = (this.antCount[ti] as number) - 1;
    }
  }

  // --- Movement / collision ---------------------------------------------------

  /**
   * True if `id` (an ant) may step onto (tx,ty): in bounds, passable, and the
   * target tile is below the ant capacity cap. Pure query — no mutation.
   */
  canMoveAnt(tx: number, ty: number): boolean {
    if (!this.inBounds(tx, ty)) return false;
    const ti = this.index(tx, ty);
    if (this.passable[ti] === 0) return false;
    if ((this.antCount[ti] as number) >= this.maxAntsPerTile) return false;
    return true;
  }

  /**
   * Move an entity from its current tile to (tx,ty), updating both buckets and
   * the entity's stored position. O(1). Caller must have checked canMoveAnt for
   * ants. Returns nothing; assumes (tx,ty) is in bounds.
   */
  move(id: EntityId, tx: number, ty: number): void {
    this.remove(id);
    this.entities.posX[id] = tx;
    this.entities.posY[id] = ty;
    this.add(id);
  }
}
